/**
 * Whether the person's sandbox is waking, and why the person waits, for "Waking your sandbox…" and the banners
 * (components/sandbox/SandboxWaking.jsx). One store for the whole page, one poll of `GET /api/v1/sandbox/state`
 * (SandboxService getSandboxState, answered at once by the sandbox gateway; it never wakes the sandbox):
 * - it polls while a component watches (`watch: true`, e.g. the Projects page while its list loads), while a request
 *   to the sandbox has waited more than SLOW_MS (utils/sandboxPending.js), while the last answer said waking, Flow
 *   updating or a block that clears by itself, and while the last try failed because Flow did not answer;
 * - a failed try is tried again after 4, 6, 8, 10 s, then every 10 s (utils/waitingState.js retryDelayMs); offline it
 *   waits for the browser's `online` event and asks at once then;
 * - it records the connection's signals next to the answer: navigator.onLine, the last failure (status, or none when
 *   nothing answered), how long the answer took (a try still in flight after SLOW_ANSWER_MS counts as slow), and a new
 *   Flow version (utils/staleBuild.js FLOW_UPDATED_EVENT, or `flow.version` changing once a rollout is over);
 * - when a wake it saw ends (running again), it sends `flow:sandbox-awake` on window, so pages fetch again what waited;
 * - retry(): `POST /api/v1/sandbox/wake` (starts it again when a start failed or took too long) and polls at once.
 *
 * useSandboxWake({ watch, inline, flowBanner }) -> { status, waiting, waking, failed, elapsedMs, online, retry,
 * retrying, retryError }. `waiting` is utils/waitingState.js's answer (null: nothing to wait for). `elapsedMs` counts
 * on locally between answers. `inline` (a page shows the full card itself): the app-wide banners stay hidden
 * meanwhile. `flowBanner`: the "Flow was updated" banner is on the page (staleBuild's own bar then stays away).
 */
import { useCallback, useEffect, useState } from 'react';
import { getSandboxState, wakeSandbox } from '../services/SandboxService';
import { slowest, subscribe as onPending } from '../utils/sandboxPending';
import { FLOW_UPDATED_EVENT, flowUpdateSeen } from '../utils/staleBuild';
import {
  SLOW_ANSWER_MS, classifyFailure, normalizeSandboxState, retryDelayMs, waitingState,
} from '../utils/waitingState';

export const SLOW_MS = 1500;
export const POLL_MS = 1500;
export const FLOW_UPDATING_POLL_MS = 5000;
export const AWAKE_EVENT = 'flow:sandbox-awake';

const browserOnline = () => (typeof navigator === 'undefined' || navigator.onLine !== false);

const initial = () => ({
  status: null,
  receivedAt: 0,
  firstSeen: 0,
  watchers: 0,
  inline: 0,
  flowBanners: 0,
  timer: null,
  polling: false,
  unsupported: false,
  online: browserOnline(),
  failure: null,
  tries: 0,
  latencyMs: 0,
  latencyAt: 0,
  newVersion: false,
  flowVersion: null,
});

const store = { ...initial(), listeners: new Set() };

function emit() {
  for (const listener of [...store.listeners]) { listener(); }
}

const isWaking = (status) => normalizeSandboxState(status)?.phase === 'waking';

/** Answers that clear by themselves: keep asking so the page learns when. */
function settlesByItself(status) {
  const s = normalizeSandboxState(status);
  if (!s) { return false; }
  return s.flow.updating || s.phase === 'flow_updating' || (s.phase === 'blocked' && s.youNeedToAct === false);
}

const failingQuietly = () => ['flow_down', 'no_answer'].includes(classifyFailure(store.failure));

function wanted() {
  if (store.unsupported || !store.online) { return false; }
  return store.watchers > 0 || slowest() >= SLOW_MS || isWaking(store.status) || settlesByItself(store.status)
    || failingQuietly();
}

function nextDelay() {
  if (store.failure) { return retryDelayMs(store.tries); }
  if (!isWaking(store.status) && settlesByItself(store.status)) { return FLOW_UPDATING_POLL_MS; }
  return POLL_MS;
}

function noteVersion(next) {
  const flow = next?.flow;
  if (!flow?.version || flow.updating) { return; }
  if (!store.flowVersion) { store.flowVersion = flow.version; return; }
  if (flow.version !== store.flowVersion) { store.newVersion = true; }
}

function receive(next) {
  const was = store.status;
  const now = Date.now();
  const s = normalizeSandboxState(next);
  if (s?.elapsedSeconds !== null && s?.elapsedSeconds !== undefined && isWaking(next)) {
    store.firstSeen = now - (s.elapsedSeconds * 1000);
  } else if (isWaking(next) && !isWaking(was)) {
    store.firstSeen = now;
  }
  store.status = next;
  store.receivedAt = now;
  emit();
  const wasWaiting = was?.state === 'waking' || isWaking(was);
  if (wasWaiting && next?.state === 'running' && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AWAKE_EVENT));
  }
}

async function pollOnce() {
  if (store.polling) { return; }
  store.polling = true;
  const started = Date.now();
  // Still no answer after SLOW_ANSWER_MS: that alone says the connection (or Flow) is slow.
  const slowTimer = setTimeout(() => {
    if (!store.polling) { return; }
    store.latencyMs = Date.now() - started;
    store.latencyAt = Date.now();
    emit();
  }, SLOW_ANSWER_MS);
  try {
    const next = await getSandboxState();
    store.latencyMs = Date.now() - started;
    store.latencyAt = Date.now();
    store.failure = null;
    store.tries = 0;
    if (next === null) { store.unsupported = true; }
    noteVersion(next);
    receive(next);
  } catch (error) {
    // Signed out, the gateway restarting, no network: keep the last answer, note why; the next tick asks again.
    const status = error?.status ?? (error instanceof TypeError ? 0 : null);
    store.failure = { status, message: error?.message || '', at: Date.now() };
    store.tries += 1;
    store.latencyMs = 0;
    emit();
  } finally {
    clearTimeout(slowTimer);
    store.polling = false;
  }
}

function schedule() {
  if (store.timer || !wanted()) { return; }
  store.timer = setTimeout(async() => {
    store.timer = null;
    if (!wanted()) { return; }
    await pollOnce();
    schedule();
  }, nextDelay());
}

/** Asks now (a fresh look), then keeps asking while wanted. */
function pollNow() {
  clearTimeout(store.timer);
  store.timer = null;
  return pollOnce().then(schedule);
}

/** A sandbox request started or ended: look once it has waited SLOW_MS. */
let slowTimer = null;
onPending(() => {
  if (slowTimer || store.unsupported) { return; }
  slowTimer = setTimeout(() => {
    slowTimer = null;
    if (slowest() >= SLOW_MS && !store.timer) {
      pollOnce().then(schedule);
    }
  }, SLOW_MS);
});

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => { store.online = false; emit(); });
  window.addEventListener('online', () => {
    store.online = true;
    store.failure = null;
    store.tries = 0;
    emit();
    if (store.watchers > 0 || store.status) { pollNow(); }
  });
  // A chunk of this build is gone (utils/staleBuild.js): a new Flow version is out. The banner shows it, so the
  // plain fallback bar stays away while the banner is on the page.
  window.addEventListener(FLOW_UPDATED_EVENT, (event) => {
    store.newVersion = true;
    if (store.flowBanners > 0 || store.inline > 0) { event.preventDefault(); }
    emit();
  });
}

/** Tests only. */
export function resetSandboxWake() {
  clearTimeout(store.timer);
  clearTimeout(slowTimer);
  slowTimer = null;
  Object.assign(store, initial());
}

/** Whether a page shows the full waking card itself (the app-wide banner then hides). */
export function inlineShown() {
  return store.inline > 0;
}

/** The waiting answer for the store as it is now. */
function currentWaiting(now) {
  const waking = isWaking(store.status);
  const elapsedMs = waking ? Math.max(0, now - (store.firstSeen || store.receivedAt)) : 0;
  const waiting = waitingState({
    online: store.online,
    status: store.status,
    failure: store.failure,
    tries: store.tries,
    latencyMs: store.latencyMs,
    latencyAt: store.latencyAt,
    newVersion: store.newVersion || flowUpdateSeen(),
    elapsedSeconds: waking ? Math.round(elapsedMs / 1000) : null,
    now,
  });
  return { waking, elapsedMs, waiting };
}

export default function useSandboxWake({ watch = false, inline = false, flowBanner = false } = {}) {
  const [, setTick] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState(null);

  useEffect(() => {
    const listener = () => setTick((n) => n + 1);
    store.listeners.add(listener);
    return () => { store.listeners.delete(listener); };
  }, []);

  useEffect(() => {
    if (!watch) { return undefined; }
    store.watchers += 1;
    if (!store.timer) { pollOnce().then(schedule); }
    return () => { store.watchers -= 1; };
  }, [watch]);

  useEffect(() => {
    if (!inline) { return undefined; }
    store.inline += 1;
    emit();
    return () => { store.inline -= 1; emit(); };
  }, [inline]);

  useEffect(() => {
    if (!flowBanner) { return undefined; }
    store.flowBanners += 1;
    return () => { store.flowBanners -= 1; };
  }, [flowBanner]);

  const { waking, elapsedMs, waiting } = currentWaiting(Date.now());
  // The elapsed time moves on between answers, and old signals expire: one tick a second while there is a wait.
  const ticking = Boolean(waiting);
  useEffect(() => {
    if (!ticking) { return undefined; }
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [ticking]);

  const retry = useCallback(async() => {
    setRetrying(true);
    setRetryError(null);
    try {
      const next = await wakeSandbox();
      if (next) { receive(next); }
    } catch (error) {
      if (error?.code || error?.status === 429) {
        receive({ state: 'failed', error: error.message, code: error.code || 'limit', status: error.status });
      }
      setRetryError(error?.message || 'Your sandbox could not be woken.');
    } finally {
      setRetrying(false);
      schedule();
    }
  }, []);

  const { status } = store;
  return {
    status,
    waiting,
    waking,
    failed: waiting?.kind === 'blocked',
    elapsedMs,
    online: store.online,
    retry,
    retrying,
    retryError,
  };
}
